/**
 * Composant de gestion des fournisseurs
 * Design V2 aligné sur ClientsManagementResponsive
 */
import { ArrowRight, Building2, Download, Edit3, Lock, Mail, MapPin, MoreVertical, Phone, Plus, Trash2, Unlock, X, CreditCard, FileText, Hash } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSuppliers } from '@/hooks/useSuppliers';
import type { Supplier } from '@/types/models';
import { confirm } from '@/stores/confirmStore';
import { notify } from '@/stores/notificationStore';
import { FilterBar, type FilterOption } from '@/components/ui';
import { Button, IconButton } from '@/components/ui-v2';
import { logger } from '@/utils/logger';
import { useExportSuppliers } from '@/features/catalog/hooks/useExportSuppliers';

interface SupplierFormData {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  countryCode: string;
  vatNumber: string;
  sirenSiret: string;
  defaultPaymentTermsDays: number;
  iban: string;
  bic: string;
  notes: string;
  isActive: boolean;
}

export function SuppliersManagement() {
  const { t } = useTranslation();
  const {
    suppliers,
    loading,
    error,
    pagination,
    searchQuery,
    includeInactive,
    loadSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    activateSupplier,
    deactivateSupplier,
    setSearchQuery,
    setIncludeInactive,
  } = useSuppliers();

  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeStatusFilters, setActiveStatusFilters] = useState<string[]>([]);
  const [formData, setFormData] = useState<SupplierFormData>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zipCode: '',
    countryCode: 'FR',
    vatNumber: '',
    sirenSiret: '',
    defaultPaymentTermsDays: 30,
    iban: '',
    bic: '',
    notes: '',
    isActive: true,
  });

  // Charger les fournisseurs au montage
  useEffect(() => {
    loadSuppliers();
  }, []);

  // Recharger quand includeInactive change
  useEffect(() => {
    loadSuppliers(1);
  }, [includeInactive]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    loadSuppliers(1, value);
  };

  // Handlers pour FilterBar
  const handleStatusToggle = (status: string) => {
    setActiveStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveStatusFilters([]);
    setIncludeInactive(false);
  };

  // Options de filtres pour les statuts
  const statusFilterOptions: FilterOption[] = [
    {
      value: 'active',
      label: t('products.suppliers.filters.active'),
      icon: Building2,
      color: 'green',
      count: suppliers.filter(s => s.isActive).length,
    },
    {
      value: 'inactive',
      label: t('products.suppliers.filters.inactive'),
      icon: Building2,
      color: 'gray',
      count: suppliers.filter(s => !s.isActive).length,
    },
  ];

  const handleNewSupplier = () => {
    setEditingSupplier(null);
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      zipCode: '',
      countryCode: 'FR',
      vatNumber: '',
      sirenSiret: '',
      defaultPaymentTermsDays: 30,
      iban: '',
      bic: '',
      notes: '',
      isActive: true,
    });
    setShowForm(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      companyName: supplier.companyName || '',
      contactName: supplier.contactName || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      addressLine1: supplier.addressLine1 || '',
      addressLine2: supplier.addressLine2 || '',
      city: supplier.city || '',
      zipCode: supplier.zipCode || '',
      countryCode: supplier.countryCode || 'FR',
      vatNumber: supplier.vatNumber || '',
      sirenSiret: supplier.sirenSiret || '',
      defaultPaymentTermsDays: supplier.defaultPaymentTermsDays || 30,
      iban: supplier.iban || '',
      bic: supplier.bic || '',
      notes: supplier.notes || '',
      isActive: supplier.isActive ?? true,
    });
    setShowForm(true);
    setActiveMenu(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.companyName.trim()) {
      notify.warning(t('products.suppliers.companyNameRequired'), t('products.suppliers.fieldRequired'));
      return;
    }

    try {
      if (editingSupplier) {
        await updateSupplier(editingSupplier.id, formData);
        notify.success(t('products.suppliers.updateSuccess'), t('products.suppliers.updateSuccessTitle'));
      } else {
        await createSupplier(formData);
        notify.success(t('products.suppliers.createSuccess'), t('products.suppliers.createSuccessTitle'));
      }
      setShowForm(false);
    } catch (err) {
      logger.error('Error saving supplier:', err);
      notify.error(t('products.suppliers.saveError'), t('products.suppliers.errorTitle'));
    }
  };

  const handleDeleteSupplier = async (supplierId: string, supplierName: string) => {
    const confirmed = await confirm({
      title: t('products.suppliers.deleteTitle'),
      message: t('products.suppliers.deleteConfirm', { name: supplierName }),
      variant: 'danger',
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
    });
    if (!confirmed) return;

    try {
      await deleteSupplier(supplierId);
      notify.success(t('products.suppliers.deleteSuccess'), t('products.suppliers.deleteSuccessTitle'));
      setActiveMenu(null);
    } catch (err) {
      logger.error('Error deleting supplier:', err);
      notify.error(t('products.suppliers.deleteError'), t('products.suppliers.errorTitle'));
    }
  };

  const handleToggleActive = async (supplier: Supplier) => {
    try {
      if (supplier.isActive) {
        await deactivateSupplier(supplier.id);
        notify.success(t('products.suppliers.deactivateSuccess'), t('products.suppliers.statusUpdatedTitle'));
      } else {
        await activateSupplier(supplier.id);
        notify.success(t('products.suppliers.activateSuccess'), t('products.suppliers.statusUpdatedTitle'));
      }
      setActiveMenu(null);
    } catch (err) {
      logger.error('Error toggling supplier status:', err);
      notify.error(t('products.suppliers.statusError'), t('products.suppliers.errorTitle'));
    }
  };

  // Filtrage côté frontend
  const filteredSuppliers = suppliers.filter((supplier) => {
    if (activeStatusFilters.length > 0) {
      if (activeStatusFilters.includes('active') && !supplier.isActive) return false;
      if (activeStatusFilters.includes('inactive') && supplier.isActive) return false;
      if (activeStatusFilters.length === 2) return true; // Both selected = show all
    }
    return true;
  });

  // Export CSV (architecture 3 couches : Composant → Hook → Service API)
  const { exportCSV, isExporting } = useExportSuppliers();

  const handleExportCSV = () => {
    exportCSV({
      search: searchQuery || undefined,
      includeInactive,
    });
  };

  return (
    <div className="px-8 pt-8 pb-12 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900 flex items-center gap-3">
            <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-teal-600" />
            {t('products.suppliers.title')}
          </h1>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {t('products.suppliers.totalCount', { count: pagination.total })}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {suppliers.length > 0 && (
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={isExporting}
              icon={<Download className="w-4 h-4" />}
            >
              {isExporting ? t('common.export.exporting') : t('common.exportCSV')}
            </Button>
          )}
          <Button
            onClick={handleNewSupplier}
            variant="secondary"
            icon={<Plus className="w-4 h-4" />}
          >
            {t('products.suppliers.newSupplier')}
          </Button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <FilterBar
        searchPlaceholder={t('products.suppliers.search')}
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        statusFilters={statusFilterOptions}
        activeStatuses={activeStatusFilters}
        onStatusToggle={handleStatusToggle}
        onClearFilters={handleClearFilters}
        totalItems={suppliers.length}
        filteredItems={filteredSuppliers.length}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-2xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && !suppliers.length ? (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600">{t('products.suppliers.loadingSuppliers')}</p>
          </div>
        </div>
      ) : filteredSuppliers.length === 0 ? (
        /* Empty state */
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-12 text-center border-2 border-dashed border-gray-300/50">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-600 mb-4">{t('products.suppliers.noSuppliersFound')}</p>
          <p className="text-gray-500 mb-6">
            {searchQuery || activeStatusFilters.length > 0
              ? t('products.suppliers.noSuppliersFilterDesc')
              : t('products.suppliers.createFirstDesc')}
          </p>
          <div className="flex justify-center">
            <Button
              onClick={handleNewSupplier}
              variant="secondary"
              size="lg"
              icon={<Plus className="w-5 h-5" />}
            >
              {t('products.suppliers.createSupplier')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Vue Mobile - Cartes */}
          <div className="md:hidden space-y-3">
            {filteredSuppliers.map((supplier) => (
              <div
                key={supplier.id}
                className={`bg-white/20 backdrop-blur-xl border border-white/30 hover:bg-white/35 hover:shadow-2xl transition-all duration-300 rounded-3xl overflow-hidden ${
                  !supplier.isActive ? 'opacity-60' : ''
                }`}
              >
                <div className="bg-white/10 backdrop-blur-sm p-4 border-b border-white/20">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-5 h-5 text-gray-500" />
                        <h3 className="text-lg font-bold text-gray-900">{supplier.companyName}</h3>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        supplier.isActive
                          ? 'bg-teal-50 text-teal-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {supplier.isActive ? t('products.suppliers.filters.active') : t('products.suppliers.filters.inactive')}
                      </span>
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => setActiveMenu(activeMenu === supplier.id ? null : supplier.id)}
                        className="p-2 hover:bg-white/30 hover:backdrop-blur-sm rounded-lg transition-all duration-300"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-600" />
                      </button>
                      {activeMenu === supplier.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setActiveMenu(null)}
                          />
                          <div className="absolute right-0 top-full mt-1 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 py-1 min-w-[160px] z-20">
                            <button
                              onClick={() => handleEditSupplier(supplier)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Edit3 className="w-4 h-4" />
                              {t('common.actions.edit')}
                            </button>
                            <button
                              onClick={() => handleToggleActive(supplier)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              {supplier.isActive ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                              {supplier.isActive ? t('common.actions.deactivate') : t('common.actions.activate')}
                            </button>
                            <button
                              onClick={() => handleDeleteSupplier(supplier.id, supplier.companyName)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('common.actions.delete')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  {supplier.contactName && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-gray-500">{t('products.suppliers.table.contact')}:</span>
                      <span>{supplier.contactName}</span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <a href={`mailto:${supplier.email}`} className="hover:text-teal-600 truncate">
                        {supplier.email}
                      </a>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <a href={`tel:${supplier.phone}`} className="hover:text-teal-600">
                        {supplier.phone}
                      </a>
                    </div>
                  )}
                  {supplier.city && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span>{supplier.zipCode} {supplier.city}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Vue Desktop - Tableau */}
          <div className="hidden md:block bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/10 backdrop-blur-sm border-b border-white/20">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('products.suppliers.table.company')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('products.suppliers.table.contact')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('products.suppliers.table.coordinates')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('products.suppliers.table.siret')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('products.suppliers.table.status')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('products.suppliers.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className={`hover:bg-white/10 transition-all duration-300 ${!supplier.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Building2 className="w-5 h-5 text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900">{supplier.companyName}</div>
                            {supplier.vatNumber && (
                              <div className="text-xs text-gray-500">{t('products.suppliers.form.vatPrefix')}: {supplier.vatNumber}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {supplier.contactName || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 space-y-1">
                          {supplier.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-3 h-3 text-gray-400" />
                              <a href={`mailto:${supplier.email}`} className="hover:text-teal-600">
                                {supplier.email}
                              </a>
                            </div>
                          )}
                          {supplier.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span>{supplier.phone}</span>
                            </div>
                          )}
                          {supplier.city && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3 h-3 text-gray-400" />
                              <span>{supplier.zipCode} {supplier.city}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {supplier.sirenSiret || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          supplier.isActive ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {supplier.isActive ? t('products.suppliers.filters.active') : t('products.suppliers.filters.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <IconButton
                            onClick={() => handleEditSupplier(supplier)}
                            variant="teal"
                            size="sm"
                            aria-label={t('common.actions.edit')}
                          >
                            <Edit3 className="w-4 h-4" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleToggleActive(supplier)}
                            variant="gray"
                            size="sm"
                            aria-label={supplier.isActive ? t('common.actions.deactivate') : t('common.actions.activate')}
                          >
                            {supplier.isActive ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteSupplier(supplier.id, supplier.companyName)}
                            variant="red"
                            size="sm"
                            aria-label={t('common.actions.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => loadSuppliers(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white/60 rounded-xl hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('products.suppliers.pagination.previous')}
                </button>
                <span className="text-sm text-gray-600">
                  {t('products.suppliers.pagination.pageOf', { page: pagination.page, pages: pagination.pages })}
                </span>
                <button
                  onClick={() => loadSuppliers(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white/60 rounded-xl hover:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('products.suppliers.pagination.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Formulaire Modal */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowForm(false)}
        >
          <div
            className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50/50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-teal-100 rounded-2xl">
                    <Building2 className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingSupplier ? t('products.suppliers.edit') : t('products.suppliers.newSupplier')}
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {editingSupplier ? t('products.suppliers.updateInfo') : t('products.suppliers.addNew')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowForm(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Informations principales */}
                  <div className="space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                    <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {t('products.suppliers.form.mainInfo')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('products.suppliers.form.companyName')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.companyName')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('products.suppliers.form.contactName')}
                      </label>
                      <input
                        type="text"
                        value={formData.contactName}
                        onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.contactName')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.email')}</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.email')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.phone')}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.phone')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Adresse */}
                  <div className="space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                    <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {t('products.suppliers.form.address')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.addressLine1')}</label>
                      <input
                        type="text"
                        value={formData.addressLine1}
                        onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.addressLine1')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.addressLine2')}</label>
                      <input
                        type="text"
                        value={formData.addressLine2}
                        onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.addressLine2')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.zipCode')}</label>
                        <input
                          type="text"
                          value={formData.zipCode}
                          onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                          placeholder={t('products.suppliers.form.placeholders.zipCode')}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.city')}</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder={t('products.suppliers.form.placeholders.city')}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.countryCode')}</label>
                      <input
                        type="text"
                        value={formData.countryCode}
                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value.toUpperCase() })}
                        placeholder={t('products.suppliers.form.placeholders.countryCode')}
                        maxLength={2}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all uppercase"
                      />
                    </div>
                  </div>

                  {/* Informations fiscales */}
                  <div className="space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                    <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      {t('products.suppliers.form.fiscalInfo')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.siret')}</label>
                      <input
                        type="text"
                        value={formData.sirenSiret}
                        onChange={(e) => setFormData({ ...formData, sirenSiret: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.siret')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.vatNumber')}</label>
                      <input
                        type="text"
                        value={formData.vatNumber}
                        onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.vatNumber')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.paymentTerms')}</label>
                      <input
                        type="number"
                        value={formData.defaultPaymentTermsDays}
                        onChange={(e) => setFormData({ ...formData, defaultPaymentTermsDays: parseInt(e.target.value) || 30 })}
                        placeholder={t('products.suppliers.form.placeholders.paymentTerms')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Informations bancaires */}
                  <div className="space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                    <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      {t('products.suppliers.form.bankInfo')}
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.iban')}</label>
                      <input
                        type="text"
                        value={formData.iban}
                        onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.iban')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('products.suppliers.form.bic')}</label>
                      <input
                        type="text"
                        value={formData.bic}
                        onChange={(e) => setFormData({ ...formData, bic: e.target.value })}
                        placeholder={t('products.suppliers.form.placeholders.bic')}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="md:col-span-2 space-y-4 bg-white/40 border border-white/50 rounded-2xl p-5">
                    <h3 className="text-sm font-semibold text-teal-700 uppercase tracking-wider flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('products.suppliers.form.notes')}
                    </h3>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      placeholder={t('products.suppliers.internalNotesPlaceholder')}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all resize-none"
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Footer - Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50/50">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-3 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-all duration-300"
              >
                {t('common.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  const form = document.querySelector('form');
                  if (form) {
                    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                }}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2"
              >
                {editingSupplier ? t('products.suppliers.form.update') : t('products.suppliers.form.create')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuppliersManagement;
